/**
 * MenuDrawer — vanilla custom element (no Lit render).
 *
 * Responsibilities:
 *  - Toggle the `menu-drawer-open` class and `inert` attribute on open/close
 *  - Wire up the close button, outside-click, and open button (#menu-drawer-open-btn)
 *  - Manage blur overlay and scroll lock (same pattern as CartDrawer)
 *  - Listen for `close-menu` custom event dispatched by StickyHeader
 */

class MenuDrawer extends HTMLElement {
  private _handleOutsideClick = (event: MouseEvent) => {
    if (!this.classList.contains("menu-drawer-open")) return;
    const target = event.target as Element;
    if (
      !target.closest("menu-drawer") &&
      !target.closest("#menu-drawer-open-btn")
    ) {
      this._close();
    }
  };

  connectedCallback() {
    const closeBtn = document.getElementById("menu-drawer-close-btn");
    closeBtn && closeBtn.addEventListener("click", () => this._close());

    const openBtn = document.getElementById("menu-drawer-open-btn");
    openBtn && openBtn.addEventListener("click", () => this._open());

    this.addEventListener("close-menu", () => this._close());

    window.addEventListener("click", this._handleOutsideClick);
  }

  disconnectedCallback() {
    window.removeEventListener("click", this._handleOutsideClick);
  }

  private _open() {
    this.classList.add("menu-drawer-open");
    this.inert = false;
    this.removeAttribute("aria-hidden");

    const openBtn = document.getElementById("menu-drawer-open-btn");
    if (openBtn) openBtn.setAttribute("aria-expanded", "true");

    this._setBackgroundHidden(true);
    const knocking = (window as any).knocking;
    if (knocking) {
      knocking.showBlur && knocking.showBlur();
      knocking.disableScroll && knocking.disableScroll();
    }
  }

  private _close() {
    this.classList.remove("menu-drawer-open");
    this.inert = true;

    const openBtn = document.getElementById("menu-drawer-open-btn");
    if (openBtn) openBtn.setAttribute("aria-expanded", "false");

    this._setBackgroundHidden(false);

    const cartDrawerOpen = document.querySelector(".cart-drawer-open");
    if (!cartDrawerOpen) {
      const knocking = (window as any).knocking;
      if (knocking) {
        knocking.hideBlur && knocking.hideBlur();
        knocking.enableScroll && knocking.enableScroll();
      }
    }
  }

  private _setBackgroundHidden(hide: boolean) {
    // Don't set #shopify-section-header inert — it contains the menu-drawer
    const selectors = [
      "#shopify-section-announcement-bar",
      "main#MainContent",
      "#shopify-section-footer",
    ];
    selectors.forEach((sel) => {
      const el = document.querySelector<HTMLElement>(sel);
      if (!el) return;
      if (hide) {
        el.setAttribute("aria-hidden", "true");
        try {
          el.inert = true;
        } catch (_) {}
      } else {
        el.removeAttribute("aria-hidden");
        try {
          el.inert = false;
        } catch (_) {}
      }
    });
  }
}

customElements.define("menu-drawer", MenuDrawer);
